import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AfterViewInit, Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GrowlerService } from '../../../../services/growler.service';
import { LoginRequest } from '../../../../services/auth/login-request.model';
import environment from '../../../../../environments/environment';
import { AuthService, LoginResponse } from '../../../../services/auth/auth.service';
import { Confirm2faCodeComponent, ConfirmTwoFactorInput, ConfirmTwoFactorOutput } from '../../../app-layout/settings/user/security/confirm-2fa-code/confirm-2fa-code.component';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { ValidationMessageService } from '../../../../services/validation-message.service';
import { MatDialog } from '@angular/material/dialog';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { DOCUMENT } from '@angular/common';
import { GtmService } from '../../../../services/gtm.service';

@UntilDestroy()
@Component({
    selector: 'app-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: [
        '../../auth-layout-shared-styles.scss',
        './login-form.component.scss',
    ],
})
export class LoginFormComponent implements OnInit, AfterViewInit
{
    @Input() loginForm: FormGroup;
    @Input() header: string;
    @Input() showSignupLink = false;

    // username login input
    @ViewChild('loginInput') loginInputRef: ElementRef;
    // password login input
    @ViewChild('loginPasswordInput') loginPasswordInputRef: ElementRef;

    isForwarding = false;
    isSubmitting = false;
    maintenanceWindow = environment.MaintenanceWindow;

    constructor(
            private _fb: FormBuilder,
            private _validationMessageService: ValidationMessageService,
            private _activatedRoute: ActivatedRoute,
            private _router: Router,
            private _route: ActivatedRoute,
            private _authService: AuthService,
            private _dialog: MatDialog,
            private _growler: GrowlerService,
            private _gtm: GtmService,
            @Inject(DOCUMENT) private document: Document,
    ) {
    }

    ngOnInit() {
        this._activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe({
            next: (params) => {
                if (params['nonce'] && params['orgId']) {
                    this.isSubmitting = true;
                    this._authService.nonceLogin(params['nonce'], params['orgId']).pipe(untilDestroyed(this)).subscribe({
                        next: (data: any) => {
                            // Store the user's JWT in local storage.
                            if (data.hasOwnProperty('token')) {
                                LegFiJwtService.store(data['token']);
                            }

                            if (typeof params['uri'] !== 'undefined' && params['uri'] !== null && params['uri'].length > 0) {
                                // noinspection JSIgnoredPromiseFromCall
                                this._router.navigateByUrl(decodeURIComponent(params['uri']));
                            } else {
                                // if this is an owner but not admin redirect to their unit
                                const jwt = LegFiJwtService.read();
                                if (jwt.unitId && !jwt.admin) {
                                    // noinspection JSIgnoredPromiseFromCall
                                    this._router.navigate(['/app/unit/detail/' + jwt.unitId]);
                                } else {
                                    // noinspection JSIgnoredPromiseFromCall
                                    this._router.navigate(['/app/owner/dashboard']);
                                }
                            }
                        },
                        error: (err: HttpErrorResponse) => {
                            const expiredLinkMessage = 'This link has expired, please log in to continue to your targeted page.';
                            this._growler.error('Error', this._validationMessageService.extractResponseErrorText(err) || expiredLinkMessage);
                            this.isSubmitting = false;
                        },
                    });
                } else {
                    if (LegFiJwtService.read()) {
                        this.isForwarding = true;
                        this.successfulLogin();
                    }
                }
            },
        });

        if (this.loginForm) {
            this.loginForm.addControl('email', this._fb.control('', [
                Validators.required,
                Validators.email,
            ]));
            this.loginForm.addControl('password', this._fb.control('', [Validators.required]));
            this.loginForm.addControl('siteId', this._fb.control(environment.SiteId));
        }
    }

    ngAfterViewInit() {
        this.loginInputRef?.nativeElement.focus();
    }

    login(checkFormGroup = false) {
        if (checkFormGroup) {
            for (const key in this.loginForm.controls) {
                if (this.loginForm.controls.hasOwnProperty(key)) {
                    this.loginForm.get(key).markAsTouched();
                    this.loginForm.get(key).updateValueAndValidity();
                }
            }

            if (this.loginForm.invalid) {
                this._growler.error('Error', 'Please check all required fields.');
                return;
            }
        } else {
            // keyboard nav to progress until submitLogin
            if (this.loginForm.get('email').invalid) {
                this._growler.error('Error', 'Please check all required fields.');
                return;
            }

            if (this.loginInputRef.nativeElement === this.document.activeElement) {
                this.loginPasswordInputRef.nativeElement.focus();
                return;
            }

            if (this.loginForm.get('password').invalid) {
                this._growler.error('Error', 'Please check all required fields.');
                return;
            }
        }

        this.isSubmitting = true;

        const request: LoginRequest = this.loginForm.value;
        return this.attemptLogin(request);
    }

    forgotPassword() {
        this._router.navigate(['/auth/password/request-reset-email']);
    }

    isFieldValid(controlName: string) {
        if (this.loginForm) {
            const control = this.loginForm.get(controlName);
            if (control && !control.valid && control.touched) {
                // forcing new object (for now) to trigger ngOnChanges for stacked error messages
                return Object.assign({}, control) as AbstractControl;
            }
        }
    }

    private successfulLogin() {
        const targetUri: string = this._route.snapshot.queryParamMap.get('uri');
        const loginOrgId: number = +this._route.snapshot.queryParamMap.get('orgId');

        const redirect = () => {
            if (targetUri !== null && targetUri.length > 0) {
                // noinspection JSIgnoredPromiseFromCall
                this._router.navigateByUrl(decodeURIComponent(targetUri));
            } else {
                const jwt = LegFiJwtService.read();

                if (jwt.admin || jwt.superUser) {
                    return this._router.navigate(['/app/organization/dashboard']);
                }

                if (jwt.unitId) {
                    return this._router.navigate(['/app/unit/detail/' + jwt.unitId]);
                }

                this._router.navigate(['/app/owner/dashboard']);
            }
        };

        const payload = LegFiJwtService.read();
        if (loginOrgId !== 0 && loginOrgId !== payload.orgId) {
            this._authService.switchAccounts(loginOrgId, {siteId: environment.SiteId}).subscribe({
                next: () => redirect(),
                error: () => {
                    this._growler.oops('There was an error switching organizations upon login. Please try again.');
                    redirect();
                },
            });
        } else {
            redirect();
        }
    }

    private attemptLogin(loginRequest: LoginRequest) {
        this._authService.login(loginRequest).pipe(untilDestroyed(this)).subscribe({
            next: (data: LoginResponse) => {
                // Store the user's JWT in local storage.
                if (data.hasOwnProperty('token')) {
                    LegFiJwtService.store(data['token']);
                    this._gtm.login({userId: LegFiJwtService.read().id});
                    this.successfulLogin();
                } else if (data.hasOwnProperty('twoFactor')) {
                    const twoFactorInput = {
                        userId: data.twoFactor.userId,
                    } as ConfirmTwoFactorInput;

                    if (data.twoFactor.sms && !data.twoFactor.google) {
                        twoFactorInput.type = 'sms';
                        twoFactorInput.canGoBack = false;
                    } else if (data.twoFactor.google && !data.twoFactor.sms) {
                        twoFactorInput.type = 'google';
                        twoFactorInput.canGoBack = false;
                    }

                    this._dialog.open(Confirm2faCodeComponent, {
                        width: MatDialogSizes.SM,
                        data: twoFactorInput,
                    }).afterClosed().subscribe({
                        next: (result: ConfirmTwoFactorOutput) => {
                            if (result?.success) {
                                loginRequest.twoFactorKey = result.token;
                                loginRequest.twoFactorType = result.type;
                                this.attemptLogin(loginRequest);
                            } else {
                                this._growler.oops('Two factor verification is required to login with this account.');
                                this.isSubmitting = false;
                            }
                        },
                    });
                }
            },
            error: (err: HttpErrorResponse) => {
                const accessDeniedMessage = 'We were unable to log you in.';
                this._growler.oops(this._validationMessageService.extractResponseErrorText(err) || accessDeniedMessage);
                this.isSubmitting = false;
            },
        });
    }
}
